import {
  DVIR,
  DVIRMechanicReview, PartialDVIR,
  useCreateDVIRMutation,
  useDeleteDVIRMutation,
  useUpdateDVIRMechanicReviewMutation,
  useUpdateDVIRMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {EntityMutationCallback} from "./useEntityMutation";

export type UseDVIRMutationProps = {}

export type UseDVIRMutationResult = {
  createDVIR: (dvir: PartialDVIR, onSuccess: ((dvir: DVIR) => void) | undefined) => void
  updateDVIR: (dvir: PartialDVIR, onSuccess: (() => void) | undefined) => void
  updateDVIRMechanicReview: (dvirId: string, review: DVIRMechanicReview, callback: EntityMutationCallback | undefined) => void
  deleteDVIR: (dvirId: string, onSuccess: (() => void) | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useDVIRMutation = (props: UseDVIRMutationProps = {}): UseDVIRMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createDVIR, {isLoading: isCreating}] = useCreateDVIRMutation()
  const [_updateDVIR, {isLoading: isUpdating}] = useUpdateDVIRMutation()
  const [_updateDVIRMechanicReview, {isLoading: isUpdatingMechanicReview}] = useUpdateDVIRMechanicReviewMutation()
  const [_deleteDVIR, {isLoading: isDeleting}] = useDeleteDVIRMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating || isUpdatingMechanicReview)
  }, [isUpdating, isUpdatingMechanicReview, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useDVIRUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.dvir", "DVIR"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useDVIRUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useDVIRUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.dvir", "DVIR"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useDVIRUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useDVIRUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.dvir", "DVIR"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useDVIRUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<DVIR>, onSuccess: ((dvir: DVIR) => void) | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.dvir", "DVIR") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (onSuccess) {
          onSuccess(payload)
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.dvir", "DVIR") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.dvir", "DVIR") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteConfirm = useCallback((dvirId: string, onSuccess: (() => void) | undefined = undefined) => {
    const promise = _deleteDVIR({id: dvirId}).unwrap()
    handleDeleteResult(promise, onSuccess)
  }, [_deleteDVIR, handleDeleteResult])

  const createDVIR = useCallback((dvir: PartialDVIR, onSuccess: ((dvir: DVIR) => void) | undefined) => {
    const promise = _createDVIR(dvir).unwrap()
    handleCreateResult(promise, onSuccess)
  }, [_createDVIR, handleCreateResult])

  const updateDVIR = useCallback((dvir: PartialDVIR, onSuccess: (() => void) | undefined) => {
    const promise = _updateDVIR(dvir).unwrap()
    handleUpdateResult(promise, onSuccess)
  }, [_updateDVIR, handleUpdateResult])

  const updateDVIRMechanicReview = useCallback((dvirId: string, review: DVIRMechanicReview, callback: EntityMutationCallback | undefined) => {
    const promise = _updateDVIRMechanicReview({id: dvirId, review: review}).unwrap()
    handleUpdateResult(promise, () => {
      callback?.onSuccess(undefined)
    })
  }, [_updateDVIRMechanicReview, handleUpdateResult])

  const deleteDVIR = useCallback((dvirId: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.dvir", "DVIR"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.deletedvir.confirm", "Are you sure you want to delete this dvir?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(dvirId, onSuccess)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createDVIR: createDVIR,
    updateDVIR: updateDVIR,
    updateDVIRMechanicReview: updateDVIRMechanicReview,
    deleteDVIR: deleteDVIR,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
